import { relationshipsOrganizationEditCopySetting } from './relationships-organization-edit-copy.js';
import { relationshipsOrganizationNotesCopySetting } from './relationships-organization-notes-copy.js';
import { relationshipsOrganizationDetailCopySetting } from './relationships-organization-detail-copy.js';
import { relationshipsContactEditCopySetting } from './relationships-contact-edit-copy.js';
import { relationshipsContactDetailCopySetting } from './relationships-contact-detail-copy.js';
import { relationshipsContactSnippetCopySetting } from './relationships-contact-snippet-copy.js';
import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const relationshipsContactsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(240),
    createAction: z.string().trim().min(1).max(80),
    searchLabel: z.string().trim().min(1).max(80),
    searchPlaceholder: z.string().trim().min(1).max(120),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(120),
    createError: z.string().trim().min(1).max(240),
    firstNameLabel: z.string().trim().min(1).max(80),
    lastNameLabel: z.string().trim().min(1).max(80),
    emailLabel: z.string().trim().min(1).max(80),
    phoneLabel: z.string().trim().min(1).max(80),
    typeLabel: z.string().trim().min(1).max(80),
    statusLabel: z.string().trim().min(1).max(80),
});
export const relationshipsContactsCopySetting = definePlatformSetting({
    definition: {
        key: 'relationships.copy.contacts',
        moduleId: 'relationships',
        title: 'Contacts interface copy',
        helpText: 'Controls tenant-editable labels in the Relationships contacts directory.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Contacts',
            title: 'Customers & Contacts',
            description: 'Find customers, referral partners, adjusters, owners, and other relationships.',
            createAction: 'New contact',
            searchLabel: 'Search contacts',
            searchPlaceholder: 'Search by name, email, or phone',
            emptyTitle: 'No contacts yet',
            emptyDescription: 'Add the first contact to begin the relationship history.',
            loadingLabel: 'Loading contacts…',
            createError: 'The contact could not be created.',
            firstNameLabel: 'First name',
            lastNameLabel: 'Last name',
            emailLabel: 'Email',
            phoneLabel: 'Phone',
            typeLabel: 'Type',
            statusLabel: 'Status',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: relationshipsContactsCopyValueSchema,
});
export const relationshipsProjectCustomerPanelEnabledSetting = definePlatformSetting({
    definition: {
        key: 'relationships.projects.customer-panel.enabled',
        moduleId: 'relationships',
        title: 'Project customer panel',
        helpText: 'Shows the Relationships-owned customer summary on Project Detail when available.',
        configurationDomain: 'module',
        valueType: 'boolean',
        schemaVersion: 1,
        codedFallback: true,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: z.boolean(),
});
export const relationshipsProjectCustomerCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(120),
    errorLabel: z.string().trim().min(1).max(240),
    notLinkedLabel: z.string().trim().min(1).max(240),
    customerLabel: z.string().trim().min(1).max(80),
    organizationLabel: z.string().trim().min(1).max(80),
    phoneLabel: z.string().trim().min(1).max(80),
    emailLabel: z.string().trim().min(1).max(80),
    contactDetailsLabel: z.string().trim().min(1).max(80),
    privateDetailsHiddenLabel: z.string().trim().min(1).max(180),
    openContactsAction: z.string().trim().min(1).max(80),
});
export const relationshipsProjectCustomerCopySetting = definePlatformSetting({
    definition: {
        key: 'relationships.copy.project-customer',
        moduleId: 'relationships',
        title: 'Project customer panel copy',
        helpText: 'Controls tenant-editable labels for the Relationships Project Detail contribution.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            title: 'Customer relationship',
            description: 'Live contact information provided by Relationships.',
            loadingLabel: 'Loading the linked customer…',
            errorLabel: 'The linked customer could not be loaded. The Project remains available.',
            notLinkedLabel: 'No Relationships contact is linked to this Project yet.',
            customerLabel: 'Customer',
            organizationLabel: 'Organization',
            phoneLabel: 'Phone',
            emailLabel: 'Email',
            contactDetailsLabel: 'Contact details',
            privateDetailsHiddenLabel: 'Private phone and email details are hidden for your role.',
            openContactsAction: 'Open contacts',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: relationshipsProjectCustomerCopyValueSchema,
});
export const relationshipsOrganizationsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(240),
    searchLabel: z.string().trim().min(1).max(80),
    searchPlaceholder: z.string().trim().min(1).max(120),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(120),
    createAction: z.string().trim().min(1).max(80),
    createError: z.string().trim().min(1).max(240),
    nameLabel: z.string().trim().min(1).max(80),
    typeLabel: z.string().trim().min(1).max(80),
    contactCountLabel: z.string().trim().min(1).max(80),
});
export const relationshipsOrganizationsCopySetting = definePlatformSetting({
    definition: {
        key: 'relationships.copy.organizations',
        moduleId: 'relationships',
        title: 'Organizations interface copy',
        helpText: 'Controls tenant-editable labels in the Relationships organizations directory.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Organizations',
            title: 'Organizations',
            description: 'Insurance carriers, property managers, vendors, and other partner companies.',
            searchLabel: 'Search organizations',
            searchPlaceholder: 'Search by name',
            emptyTitle: 'No organizations yet',
            emptyDescription: 'Add the first organization to group its contacts and history.',
            loadingLabel: 'Loading organizations…',
            createAction: 'New organization',
            createError: 'The organization could not be created.',
            nameLabel: 'Name',
            typeLabel: 'Type',
            contactCountLabel: 'Contacts',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: relationshipsOrganizationsCopyValueSchema,
});
export const relationshipsLeadsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(240),
    statusFilterLabel: z.string().trim().min(1).max(80),
    allStatusesLabel: z.string().trim().min(1).max(80),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(120),
    createAction: z.string().trim().min(1).max(80),
    createError: z.string().trim().min(1).max(240),
    actionError: z.string().trim().min(1).max(240),
    callerNameLabel: z.string().trim().min(1).max(80),
    callerPhoneLabel: z.string().trim().min(1).max(80),
    callerEmailLabel: z.string().trim().min(1).max(80),
    sourceLabel: z.string().trim().min(1).max(80),
    urgencyLabel: z.string().trim().min(1).max(80),
    scoreLabel: z.string().trim().min(1).max(80),
    convertAction: z.string().trim().min(1).max(80),
    inquiryAction: z.string().trim().min(1).max(80),
    cancelAction: z.string().trim().min(1).max(80),
    loseAction: z.string().trim().min(1).max(80),
    convertedLabel: z.string().trim().min(1).max(120),
});
export const relationshipsLeadsCopySetting = definePlatformSetting({
    definition: {
        key: 'relationships.copy.leads',
        moduleId: 'relationships',
        title: 'Leads interface copy',
        helpText: 'Controls tenant-editable labels in the Relationships leads screen.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Leads',
            title: 'Leads',
            description: 'Every inbound opportunity from first contact through booking or loss.',
            statusFilterLabel: 'Status',
            allStatusesLabel: 'All statuses',
            emptyTitle: 'No leads yet',
            emptyDescription: 'New calls and website inquiries will appear here.',
            loadingLabel: 'Loading leads…',
            createAction: 'New lead',
            createError: 'The lead could not be created.',
            actionError: 'That lead action could not be completed.',
            callerNameLabel: 'Caller name',
            callerPhoneLabel: 'Phone',
            callerEmailLabel: 'Email',
            sourceLabel: 'Source',
            urgencyLabel: 'Urgency',
            scoreLabel: 'Score',
            convertAction: 'Convert to project',
            inquiryAction: 'Mark inquiry',
            cancelAction: 'Cancel',
            loseAction: 'Mark lost',
            convertedLabel: 'Booked to project',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: relationshipsLeadsCopyValueSchema,
});
export const relationshipsSettingRegistrations = [
    relationshipsOrganizationNotesCopySetting,
    relationshipsOrganizationEditCopySetting,
    relationshipsOrganizationDetailCopySetting,
    relationshipsContactEditCopySetting,
    relationshipsContactSnippetCopySetting,
    relationshipsContactDetailCopySetting,
    relationshipsContactsCopySetting,
    relationshipsProjectCustomerPanelEnabledSetting,
    relationshipsProjectCustomerCopySetting,
    relationshipsOrganizationsCopySetting,
    relationshipsLeadsCopySetting,
];
//# sourceMappingURL=relationships-setting-definitions.js.map